<script setup lang="ts">
/**
 * Development-only controls: the role switcher and the interaction-state switch.
 *
 * It exists because both things `FND-11` is judged on are otherwise unobservable
 * against the fakes: role gates need three accounts to exercise without three
 * real Cognito users, and a declared empty state that nobody can reach is a
 * paragraph in a registry.
 *
 * Two gates, not one. `import.meta.env.DEV` keeps it out of the production bundle
 * entirely, and `session.canSwitchRole` is false for any `AuthClient` that cannot
 * mint a role — so if this were ever shipped, it would render nothing rather than
 * offering a role picker to the internet. The scenario switch is additionally
 * gated to the fake data source, because it drives the fake and a control that
 * silently does nothing against the live API would read as broken.
 */

import { computed } from 'vue'

import MpCombobox from '@/components/base/MpCombobox.vue'
import { SCENARIO_LABELS, SCENARIOS, configuredDataSource, scenario, setScenario, type Scenario } from '@/lib/api'
import { ROLES, ROLE_LABELS, type Role } from '@/lib/auth/contract'
import { useSessionStore } from '@/stores/session'

const session = useSessionStore()

const visible = import.meta.env.DEV && session.canSwitchRole
const scenarioVisible = configuredDataSource() === 'fake'

const roleOptions = ROLES.map(role => ({ id: role, label: ROLE_LABELS[role] }))
const scenarioOptions = SCENARIOS.map(option => ({ id: option, label: SCENARIO_LABELS[option] }))

/**
 * Both switches read and write through a computed rather than a change handler.
 *
 * The cast on the way in is the same one the handlers did: these lists are built
 * from `ROLES` and `SCENARIOS`, so the id coming back is always one of them, and
 * the combobox's own contract is that it only ever returns an id it was given.
 */
const role = computed({
  get: () => session.role ?? '',
  set: (next: string) => {
    void session.switchRole(next as Role)
  }
})

const dataScenario = computed({
  get: () => scenario.value,
  set: (next: string) => {
    setScenario(next as Scenario)
  }
})
</script>

<template>
  <div
    v-if="visible"
    class="mp-dev"
  >
    <span class="mp-dev__tag">Local</span>

    <label class="mp-dev__field">
      <span>Role</span>
      <MpCombobox
        v-model="role"
        aria-label="Role"
        :options="roleOptions"
      />
    </label>

    <label
      v-if="scenarioVisible"
      class="mp-dev__field"
    >
      <span>Data</span>
      <MpCombobox
        v-model="dataScenario"
        aria-label="Data scenario"
        :options="scenarioOptions"
      />
    </label>
  </div>
</template>

<style scoped>
.mp-dev {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-4);
  background: var(--caution-bg);
  color: var(--caution-text);
  border-block-end: 1px solid var(--caution-text);
  font-size: var(--font-size-caption);
}

.mp-dev__tag {
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.mp-dev__field {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-weight: 600;
}

</style>
